/**
 * treatrx.client.ts
 *
 * Thin client for the deployed TreatRx/Ayva middleware
 * (https://treatrx.followthatpatient.com/api/v1/*). These calls are made
 * DIRECTLY from the browser — exactly as the legacy app did in
 * html/html/js/treatrx/patient.js — and intentionally do NOT go through
 * apiClient (no V5 Authorization / X-Target-Company headers). The middleware
 * is CORS-open and reads form-encoded bodies, so we post
 * application/x-www-form-urlencoded just like jQuery's $.ajax default.
 */

import { looksLikePhpErrorBody } from './treatrx.encounterList';
import { getLegacyOrigin } from '../lib/appStage';

const TREATRX_BASE: string =
  (import.meta.env.VITE_TREATRX_URL as string | undefined) ??
  'https://treatrx.followthatpatient.com';

/**
 * Legacy host that owns the TreatRx OAuth callback. Bravado's redirect URL is
 * registered on its side and is a single global setting, so the grant has to
 * start and finish there even for a Prospera user. Same VITE_LEGACY_APP_URL
 * the deploy workflow injects for every other legacy link — see appStage.ts.
 */
const LEGACY_BASE: string = getLegacyOrigin();

type Primitive = string | number | boolean | null | undefined;

function encodeForm(data: Record<string, Primitive>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(data)) {
    if (value === undefined || value === null) continue;
    params.append(key, String(value));
  }
  return params.toString();
}

async function post(endpoint: string, data: Record<string, Primitive>): Promise<Response> {
  return fetch(`${TREATRX_BASE}/api/v1/${endpoint}?subdomain=app`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
    body: encodeForm(data),
  });
}

/**
 * Read a TreatRx body. The middleware reports failures with `die($message)`,
 * which emits the raw text (SQL included) as text/html with HTTP 200 — so a 2xx
 * response has to be sniffed before it can be trusted.
 */
async function readBody(endpoint: string, res: Response): Promise<string> {
  const text = await res.text();
  if (!res.ok) {
    throw new Error(text || `TreatRx ${endpoint} failed (${res.status})`);
  }
  if (looksLikePhpErrorBody(text)) {
    throw new Error(text);
  }
  return text;
}

async function postJson<T = any>(endpoint: string, data: Record<string, Primitive>): Promise<T> {
  const text = await readBody(endpoint, await post(endpoint, data));
  try {
    return JSON.parse(text) as T;
  } catch {
    const snippet = text.trim().slice(0, 200);
    throw new Error(`TreatRx ${endpoint} returned a non-JSON response: ${snippet}`);
  }
}

async function postText(endpoint: string, data: Record<string, Primitive>): Promise<string> {
  return readBody(endpoint, await post(endpoint, data));
}

// ─── Shaped payloads (mirror patient.js verbatim) ────────────────────────────

export interface AyvaPatientPayload {
  dateOfBirth: string;
  firstName: string;
  lastName: string;
  mrn: string;
  middleName: string;
  gender: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  mobileNumber: string;
  email: string;
  weight: string;
  language: string;
}

// ─── Endpoints ───────────────────────────────────────────────────────────────

export function isDoctorGranted(ayvaDoctorID: string | number) {
  return postJson<{ isGranted?: boolean }>('isDoctorGranted', { ayvaDoctorID });
}

export function isPatientExists(args: {
  patientID: string | number;
  ayvaDoctorID: string | number;
  accountID: string | number;
  ftp_patientID: string | number;
} & AyvaPatientPayload) {
  return postJson<any>('isPatientExists', {
    patientID: args.patientID,
    ayvaDoctorID: args.ayvaDoctorID,
    dateOfBirth: args.dateOfBirth,
    firstName: args.firstName,
    lastName: args.lastName,
    mrn: args.mrn,
    middleName: args.middleName,
    gender: args.gender,
    street: args.street,
    city: args.city,
    state: args.state,
    zip: args.zip,
    mobileNumber: args.mobileNumber,
    email: args.email,
    weight: args.weight,
    accountID: args.accountID,
    ftp_patientID: args.ftp_patientID,
  });
}

export function createPatient(args: {
  ayvaDoctorID: string | number;
  ftp_patientID: string | number;
  accountID: string | number;
} & AyvaPatientPayload) {
  return postJson<any>('createPatient', {
    dateOfBirth: args.dateOfBirth,
    firstName: args.firstName,
    lastName: args.lastName,
    activeFlag: true,
    mrn: args.mrn,
    middleName: args.middleName,
    gender: args.gender,
    language: args.language,
    street: args.street,
    city: args.city,
    state: args.state,
    zip: args.zip,
    mobileNumber: args.mobileNumber,
    email: args.email,
    weight: args.weight,
    weightUnit: 'LBS',
    ayvaDoctorID: args.ayvaDoctorID,
    ftp_patientID: args.ftp_patientID,
    accountID: args.accountID,
  });
}

export function updatePatient(args: {
  ayvaPatientID: string | number;
  ayvaDoctorID: string | number;
  ftp_patientID: string | number;
  accountID: string | number;
} & AyvaPatientPayload) {
  return postJson<any>('updatePatient', {
    ayvaPatientID: args.ayvaPatientID,
    dateOfBirth: args.dateOfBirth,
    firstName: args.firstName,
    lastName: args.lastName,
    activeFlag: true,
    mrn: args.mrn,
    middleName: args.middleName,
    gender: args.gender,
    language: args.language,
    street: args.street,
    city: args.city,
    state: args.state,
    zip: args.zip,
    mobileNumber: args.mobileNumber,
    email: args.email,
    weight: args.weight,
    weightUnit: 'LBS',
    ayvaDoctorID: args.ayvaDoctorID,
    ftp_patientID: args.ftp_patientID,
    accountID: args.accountID,
  });
}

export function createEncounter(args: {
  ayvaDoctorID: string | number;
  patientId: string | number;
  encReference: string;
  ayvaPatientID: string | number;
  encDischargeDisp: string;
  accountID: string | number;
}) {
  return postJson<any>('createEncounter', { ...args });
}

export function getEncounterList(ayvaDoctorID: string | number, patientID: string | number) {
  return postText('getEncounterList', { ayvaDoctorID, patientID });
}

export function getPatientEncounter(ayvaDoctorID: string | number, encounterID: string | number) {
  return postJson<{ data: string; basepath: string }>('getPatientEncounter', {
    ayvaDoctorID,
    encounterID,
  });
}

/**
 * Build the "Allow Access" URL for a one-time doctor grant.
 *
 * This points at the legacy callback page, not at Bravado. The handle comes
 * from POST /patients/{id}/prescriptions/grant-flow and names a row in
 * aa_treatrx_grant_flows; the legacy shim looks up the OAuth clientId from that
 * row and redirects on to Bravado itself. Composing the Bravado URL here would
 * put the callback on a host the browser has never visited in this flow, which
 * is what breaks the grant for Prospera users today.
 */
export function buildGrantHandoffUrl(handle: string): string {
  return `${LEGACY_BASE}/treatrx.php?v5=${encodeURIComponent(handle)}`;
}
